import { type CrowdexUIMessage, isSteerMessage } from '@crowdex/agent-contracts';
import { MessageAction, MessageActions } from '@crowdex/ui/ai/message';
import { MessageCircle, Rewind, Square, Volume2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  MESSAGE_ACTION,
  MessageCopyAction,
} from '@/components/desktop-message-part/message-copy-action';
import { messageText } from '@/lib/conversations/message-part/message-text';

const ACTION = MESSAGE_ACTION;

/** Whether this window can read text aloud. */
function speechSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/**
 * Under the last reply (D5, mobile 3), always in sight: copy it, read it aloud, ask about it in
 * the side chat, or rewind to the message before it (edit it, D46).
 */
export function LastReplyActions({
  className,
  messages,
  reply,
  onEditMessage,
  onSideChat,
}: {
  className?: string;
  messages: readonly CrowdexUIMessage[];
  reply: CrowdexUIMessage;
  onEditMessage?: ((message: CrowdexUIMessage, text: string) => void) | undefined;
  /** Asks the side chat about the reply. */
  onSideChat?: ((message: CrowdexUIMessage, text: string) => void) | undefined;
}) {
  const [speaking, setSpeaking] = useState(false);
  const text = messageText(reply);
  const index = messages.indexOf(reply);
  // A steer belongs to the turn it redirected: rewinding resends the message before it.
  const asked = messages
    .slice(0, index)
    .findLast((message) => message.role === 'user' && !isSteerMessage(message));
  useEffect(
    () => () => {
      if (speechSupported()) window.speechSynthesis.cancel();
    },
    [],
  );
  if (text.length === 0) return null;
  return (
    <MessageActions
      aria-label="Reply actions"
      className={className ?? 'mt-1 justify-start'}
      role="toolbar"
    >
      <MessageCopyAction text={text} />
      {speechSupported() && (
        <MessageAction
          aria-label={speaking ? 'Stop reading' : 'Read aloud'}
          className={ACTION}
          onClick={() => {
            const synthesis = window.speechSynthesis;
            synthesis.cancel();
            if (speaking) {
              setSpeaking(false);
              return;
            }
            const utterance = new SpeechSynthesisUtterance(text);
            utterance.onend = () => setSpeaking(false);
            utterance.onerror = () => setSpeaking(false);
            setSpeaking(true);
            synthesis.speak(utterance);
          }}
          tooltip={speaking ? 'Stop reading' : 'Read aloud'}
        >
          {speaking ? <Square className="size-3.5" /> : <Volume2 className="size-3.5" />}
        </MessageAction>
      )}
      {onSideChat !== undefined && (
        <MessageAction
          aria-label="Ask in a side chat"
          className={ACTION}
          onClick={() => onSideChat(reply, text)}
          tooltip="Side chat"
        >
          <MessageCircle className="size-3.5" />
        </MessageAction>
      )}
      {onEditMessage !== undefined && asked !== undefined && (
        <MessageAction
          aria-label="Rewind"
          className={ACTION}
          onClick={() => onEditMessage(asked, messageText(asked))}
          tooltip="Rewind to the message before"
        >
          <Rewind className="size-3.5" />
        </MessageAction>
      )}
    </MessageActions>
  );
}
